/**
 * The studio footer piece: the foot of every page for a guest. The studio's
 * mark and its line, a list of the studio's sections and pages that exist,
 * where to find the studio when it has a location, and the small line. A
 * signed-in client also gets Your account: Bookings, Messages and Profile
 * (web-account-pages, the prototype's signed-in footer).
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'studio-footer',
  description:
    "The foot of every page: the studio's mark and line, its sections and pages that exist, where to find it, and the small line.",
  options: {
    linksHeading: { type: 'string', default: 'Book', maxLength: 40, description: 'The heading over the links; null for none.' },
    findUsHeading: { type: 'string', default: 'Find us', maxLength: 40, description: 'The heading over the location; null for none.' },
    showFindUs: { type: 'boolean', default: true, description: "Show where to find the studio, when it has a location.", showsPart: 'findUs' },
    accountHeading: { type: 'string', default: 'Your account', maxLength: 40, description: "The heading over a signed-in client's account links; null for none." },
  },
  parts: {
    footer: { description: 'The whole footer.', required: false },
    brand: { description: "The studio's mark and its line.", required: false },
    mark: { description: "The studio's mark; opens the home page.", required: false },
    logo: { description: "The studio's logo image, when it has one.", required: false },
    name: { description: "The studio's name, or its wordmark when it has no logo.", required: false },
    blurb: { description: "The studio's line, with its location and year when set.", required: false },
    links: { description: "The list of the studio's sections and pages.", required: false },
    linksHeading: { description: 'The heading over the links.', required: false },
    link: { description: 'One link: Services, Events or Open times.', required: false },
    account: { description: "A signed-in client's account links.", required: false },
    accountHeading: { description: 'The heading over the account links.', required: false },
    accountLink: { description: 'One account link: Bookings, Messages (opens the messages) or Profile.', required: false },
    findUs: { description: 'Where to find the studio.', required: false },
    findUsHeading: { description: 'The heading over the location.', required: false },
    findUsName: { description: "The studio's name above its location.", required: false },
    location: { description: "The studio's location line.", required: false },
    small: { description: 'The small line: the year and the studio.', required: false },
  },
  actions: {
    goHome: { description: 'Opens the home page at its top.', args: [] },
    open: { description: 'Opens one of the footer links.', args: [{ name: 'linkId', kind: 'item', list: 'links', key: 'id' }] },
    openAccount: { description: "Opens one of a signed-in client's account links; Messages opens the messages panel.", args: [{ name: 'linkId', kind: 'item', list: 'account', key: 'id' }] },
  },
  events: {
    navigated: 'A visitor followed a footer link or the mark.',
  },
});

/** Where a footer link goes. */
export type FooterLinkId = 'services' | 'events' | 'times';

export interface FooterLink {
  /** Which link this is. */
  readonly id: FooterLinkId;
  /** The words drawn: Services, Events or Open times. */
  readonly label: string;
  /** The address it opens: `/#services`, `/events` or `/#times`. */
  readonly href: string;
}

/** One of a signed-in client's account links. */
export type FooterAccountLinkId = 'bookings' | 'messages' | 'profile';

export interface FooterAccountLink {
  /** Which link this is. */
  readonly id: FooterAccountLinkId;
  /** Bookings, Messages or Profile. */
  readonly label: string;
  /** The address it opens; null for Messages, which opens the panel. */
  readonly href: string | null;
}

/** What the studio footer knows. */
export interface StudioFooterState {
  /** loading until the studio's profile is known; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The studio's name; empty while loading. */
  readonly name: string;
  /** The studio's logo image, or null to draw the name alone as the wordmark. */
  readonly logoUrl: string | null;
  /** The studio's line, then its location and year joined with " · "; null when none is set. */
  readonly blurb: string | null;
  /** The studio's location, or null when it has none (the Find us block drops out). */
  readonly location: string | null;
  /** The links to draw, only for what the studio has. */
  readonly links: readonly FooterLink[];
  /** The year in the small line. */
  readonly year: number;
  /** A signed-in client's account links; empty for a guest. */
  readonly account: readonly FooterAccountLink[];
}

/** What a drawing can ask the studio footer to do. */
export interface StudioFooterActions {
  /** Open the home page at its top. */
  readonly goHome: () => void;
  /** Open the link with this id; an id not in `links` is ignored. */
  readonly open: (linkId: FooterLinkId) => void;
  /** Open an account link; Messages opens the messages panel. */
  readonly openAccount: (linkId: FooterAccountLinkId) => void;
}

/** What the studio footer tells its host. */
export interface StudioFooterEvents {
  /** A visitor followed a link (`home` for the mark). */
  readonly navigated: { readonly linkId: FooterLinkId | FooterAccountLinkId | 'home' };
}
